import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useState } from 'react';
import { useToast } from '../../../hooks/useToast';
import { engageLibraryKeys } from './engage-library-query-keys';
import {
  EMPTY_ENGAGE_LIBRARY,
  EngageLibraryItemType,
  EngageLibraryBatchedSyncResponse,
  EngageLibraryItemTypeEnum,
  EngageLibraryRowModel,
  EngageLibrarySyncRequest,
  EngageLibrarySyncStatusEnum,
  EngageStatusFilter,
} from './engage-library-types';
import {
  categoriesInUse,
  countByStatus,
  engageLibraryRowKey,
  engageLibraryTabId,
  filterRows,
  statusFilterOptionsForType,
  syncedCount,
  toLibraryRowModels,
  toSyncBatches,
  toSyncRequest,
  withOptimisticSyncing,
} from './engage-library-utils';
import { EngageLibraryBulkSelect } from './EngageLibraryBulkSelect';
import { EngageLibrarySaveBar } from './EngageLibrarySaveBar';
import { EngageLibraryTable } from './EngageLibraryTable';
import { EngageLibraryToolbar } from './EngageLibraryToolbar';
import { EngageLibraryTypeTabs } from './EngageLibraryTypeTabs';
import { usePendingMembershipChanges } from './usePendingMembershipChanges';
import { useSyncTenantEngageLibrary } from './useSyncTenantEngageLibrary';
import { PolledJob, useTenantEngageLibrary, useTenantEngageLibraryJobs } from './useTenantEngageLibrary';
import styles from './TenantEngageTab.module.css';

const SUBTITLE =
  'Check what this tenant should have and uncheck what it should not, then save your changes.';
const EMPTY_SYNCING_IDS = new Set<string>();

interface TenantEngageTabProps {
  tenantId: string;
}

interface ActiveReconciliation {
  jobs: PolledJob[];
  itemIds: Set<string>;
}

const EMPTY_JOBS: PolledJob[] = [];

export function TenantEngageTab({ tenantId }: TenantEngageTabProps) {
  const { data: library = EMPTY_ENGAGE_LIBRARY, isLoading, isError } = useTenantEngageLibrary();
  const syncMutation = useSyncTenantEngageLibrary();
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const panelId = useId();

  const [type, setType] = useState<EngageLibraryItemType>(EngageLibraryItemTypeEnum.AUTOMATION);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<EngageStatusFilter>('all');
  const [categoryIds, setCategoryIds] = useState<string[]>([]);
  const [activeReconciliation, setActiveReconciliation] = useState<ActiveReconciliation | null>(null);
  const reconciliation = useTenantEngageLibraryJobs(activeReconciliation?.jobs ?? EMPTY_JOBS);

  const libraryRows = withOptimisticSyncing(
    toLibraryRowModels(library),
    activeReconciliation?.itemIds ?? EMPTY_SYNCING_IDS,
  );
  const pendingMembership = usePendingMembershipChanges(libraryRows);
  const allRows = libraryRows.filter(row => row.type === type);
  const categories = categoriesInUse(allRows);
  const filteredRows = filterRows(allRows, { search, statusFilter, categoryIds });
  const statusOptions = statusFilterOptionsForType(type);
  const statusCounts = countByStatus(allRows, statusOptions);
  const syncedTotal = syncedCount(allRows);
  const typeLabel = type === EngageLibraryItemTypeEnum.AUTOMATION ? 'automation' : 'segment';
  const pluralTypeLabel = `${typeLabel}${allRows.length === 1 ? '' : 's'}`;
  const syncedTypeLabel = `${typeLabel}${syncedTotal === 1 ? '' : 's'}`;
  const isOperationPending = syncMutation.isPending || activeReconciliation !== null;

  useEffect(() => {
    if (!activeReconciliation || !reconciliation.isSettled) return;

    setActiveReconciliation(null);
    queryClient.invalidateQueries({ queryKey: engageLibraryKeys.library(tenantId) });

    if (reconciliation.hasFailedJob) {
      addToast({
        title: 'Automation sync failed',
        description: 'Retry the failed automations after the library refreshes.',
        variant: 'destructive',
      });
    } else if (reconciliation.hasUnverifiedJob) {
      addToast({
        title: 'Unable to verify automation sync',
        description: 'The library will refresh, but the automation may still be syncing.',
        variant: 'destructive',
      });
    }
  }, [
    activeReconciliation,
    addToast,
    queryClient,
    reconciliation.hasFailedJob,
    reconciliation.hasUnverifiedJob,
    reconciliation.isSettled,
    tenantId,
  ]);

  const changeType = (selectedType: EngageLibraryItemType) => {
    setType(selectedType);
    setStatusFilter('all');
    setCategoryIds([]);
  };

  const trackReconciliation = (response: EngageLibraryBatchedSyncResponse) => {
    const queuedIds = response.results
      .filter(
        result =>
          result.type === EngageLibraryItemTypeEnum.AUTOMATION &&
          result.status === EngageLibrarySyncStatusEnum.QUEUED,
      )
      .map(result => result.id);
    const hasQueuedJobs = response.jobIds.length > 0 && queuedIds.length > 0;
    if (!hasQueuedJobs) return;
    const startedAt = Date.now();
    setActiveReconciliation({
      jobs: response.jobIds.map(jobId => ({ jobId, startedAt })),
      itemIds: new Set(queuedIds),
    });
  };

  const runSync = (
    requests: EngageLibrarySyncRequest[],
    onSynced?: (response: EngageLibraryBatchedSyncResponse) => void,
  ) => {
    if (isOperationPending) return;
    syncMutation.mutate(requests, {
      onSuccess: response => {
        onSynced?.(response);
        trackReconciliation(response);
      },
    });
  };

  const saveChanges = () => {
    const batches = toSyncBatches(pendingMembership.changes);
    runSync(batches.map(toSyncRequest), response => {
      const failedItemKeys = new Set(
        response.results
          .filter(result => result.status === EngageLibrarySyncStatusEnum.FAILED)
          .map(result => `${result.type}:${result.id}`),
      );
      const unsavedChanges = batches.flatMap((batch, index) => {
        if (response.failedRequestIndexes.includes(index)) return batch;
        return batch.filter(change => failedItemKeys.has(engageLibraryRowKey(change.row)));
      });
      pendingMembership.retain(unsavedChanges);
    });
  };

  const retryRow = (row: EngageLibraryRowModel) =>
    runSync([{ sync: [{ type: row.type, id: row.id }], unsync: [] }]);

  if (isLoading) return <p className={styles.stateMessage}>Loading Engage library…</p>;
  if (isError) return <p className={styles.stateMessage}>Failed to load the Engage library.</p>;

  return (
    <section className={styles.card}>
      <header className={styles.header}>
        <div className={styles.heading}>
          <h3 className={styles.title}>Automations &amp; Segments</h3>
          <p className={styles.subtitle}>{SUBTITLE}</p>
        </div>
        <div className={styles.counts}>
          <span className={styles.syncedPill}>
            {syncedTotal} {syncedTypeLabel} synced
          </span>
          <span className={styles.libraryPill}>
            {allRows.length} {pluralTypeLabel} in library
          </span>
        </div>
      </header>

      <div className={styles.toolbar}>
        <EngageLibraryTypeTabs value={type} panelId={panelId} onChange={changeType} />
        <EngageLibraryToolbar
          search={search}
          onSearchChange={setSearch}
          statusFilter={statusFilter}
          statusOptions={statusOptions}
          statusCounts={statusCounts}
          onStatusFilterChange={setStatusFilter}
          categories={categories}
          categoryIds={categoryIds}
          onCategoryIdsChange={setCategoryIds}
        />
      </div>

      <div id={panelId} role="tabpanel" aria-labelledby={engageLibraryTabId(panelId, type)}>
        <div className={styles.actionsRow}>
          <EngageLibraryBulkSelect
            rows={filteredRows}
            pendingActionByKey={pendingMembership.pendingActionByKey}
            isSaving={syncMutation.isPending}
            onSetMembership={pendingMembership.setMembership}
          />
          <EngageLibrarySaveBar
            changes={pendingMembership.changes}
            isSaving={syncMutation.isPending}
            isPending={isOperationPending}
            onSave={saveChanges}
            onDiscard={pendingMembership.discard}
          />
        </div>

        <EngageLibraryTable
          rows={filteredRows}
          pendingActionByKey={pendingMembership.pendingActionByKey}
          isSaving={syncMutation.isPending}
          isPending={isOperationPending}
          onToggleRow={pendingMembership.toggle}
          onRetry={retryRow}
        />

        {filteredRows.length > 0 && (
          <p className={styles.footer}>
            Showing {filteredRows.length} of {allRows.length}
          </p>
        )}
      </div>
    </section>
  );
}
